import { ContentType } from '../ContentType';
import { Messages } from '../../../../gen-src/Localize';
import { IContentDefinition } from './IContentDefinition';
import { CommonColumns, IColumnFactoryParameters } from '../../components/listView/ListViewGridColumnGenerator';
import { DatabaseNameCell } from '../../components/listView/dataGridCells/DatabaseNameCell';
import { LocalizedCountCell } from '../../components/listView/dataGridCells/PlainTextAndNumberCells';
import { databasesWithCrassCellFormatters } from '@tableau/catalog-ui-temp';
import { SearchResponseHitsContent } from '../../../generated/content-exploration';
import GetMetaDataRequests from '../../services/httpClient/GetMetaDataRequests';
import { GetDatabasesResult, IDatabaseContainer, IDatabase, IDataQualityIndicator } from '../httpClient/MetaDataResults';
import { Database, IDataQualityWarningInfo } from '../SearchResultTypes';
import { mapCertifier, mapDataQualityWarningOwnerToAuthor, mapDataQualityWarningUpdatedAt } from '../../utils/DataCatalogUtils';
import { SortBy } from '../SortSelection';
import { defaultHeaderFormatter } from '../../components/listView/DefaultHeaderFormatter';
import { SessionManagementService } from '../../services/SessionManagementService';
import { LocationTypes } from '../ILocationInfo';
import { FeatureFlag } from '../../services/FeatureFlagService';
import { ConnectedTagFilter } from '../../components/filters/quickFilter/TagFilter';

export const databaseContentDefinition: IContentDefinition<Database, GetDatabasesResult> = {
  getSectionTitle: () => Messages.DatabaseSectionTitle(),
  getUrl: (urls) => urls.searchDatabasesUrl(),
  getPath: (urls) => urls.searchDatabasesPath(),
  defaultDisplayMode: 'list',
  maxItemCountInMixedContentTypeResultPage: 8,
  getListViewColumns: () => [
    (parameters: IColumnFactoryParameters) => {
      return {
        name: 'databaseName',
        dataKey: 'databaseName',
        sortable: false,
        headerText: Messages.column_name(),
        growToFit: true,
        headerFormatter: defaultHeaderFormatter(),
        cellFormatter: DatabaseNameCell(parameters),
      };
    },
    (parameters: IColumnFactoryParameters) => {
      return {
        name: 'workbookscount',
        dataKey: 'workbookscount',
        sortable: false,
        direction: 'desc',
        headerText: Messages.column_numWorkbooks(),
        headerFormatter: defaultHeaderFormatter(),
        cellFormatter: LocalizedCountCell('downstreamWorkbookCount'),
      };
    },

    (parameters: IColumnFactoryParameters) => {
      return {
        name: 'datasourcecount',
        dataKey: 'datasourcecount',
        sortable: false,
        direction: 'desc',
        headerText: Messages.column_numDatasources(),
        headerFormatter: defaultHeaderFormatter(),
        cellFormatter: LocalizedCountCell('downstreamDataSourceCount'),
      };
    },
    (parameters: IColumnFactoryParameters) => {
      return {
        name: 'database',
        dataKey: 'database',
        sortable: false,
        direction: 'desc',
        growToFit: true,
        headerText: Messages.column_table_database(),
        headerFormatter: defaultHeaderFormatter(),
        cellFormatter: databasesWithCrassCellFormatters.serverOrPath.cellFormatter,
      };
    },
    CommonColumns.description,
    CommonColumns.location,
  ],
  mapCRASSData: (crassData: SearchResponseHitsContent[]) => {
    return crassData.map((crassDataItem: SearchResponseHitsContent) => {
      return {
        contentType: ContentType.database,
        id: crassDataItem.id.toString() || '',
        graphQLId: crassDataItem.graphQLId,
        name: crassDataItem.title || '',
        crassData: crassDataItem,
        description: crassDataItem.description,
      };
    });
  },
  mapMetadata: (searchResultItems, metadata) => {
    const databaseMetadata: { [id: string]: IDatabase } = {};
    const containerMetadata: { [id: string]: IDatabaseContainer } = {};
    if (metadata.total > 0) {
      metadata.items.databases.forEach((item) => {
        databaseMetadata[item.id] = item;
      });
      metadata.items.containers?.forEach((item) => {
        containerMetadata[item.id] = item;
      });
    }

    return searchResultItems.map((searchResultItem) => {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const crassDataItem = searchResultItem.crassData!;
      const databaseMetadataItem = databaseMetadata[crassDataItem.id];
      const dataQualityWarnings: IDataQualityIndicator[] = databaseMetadataItem ? databaseMetadataItem.dataQualityWarning : [];
      const dataQualityCertifications: IDataQualityIndicator[] = databaseMetadataItem ? databaseMetadataItem.dataQualityCertifications : [];

      let containerId = '';
      let containerName = '';
      let containerType = '';
      if (
        FeatureFlag.databasesInProjectsEnabled &&
        FeatureFlag.databasesInProjectsPostMVPEnabled &&
        databaseMetadataItem.containerId !== undefined
      ) {
        if (containerMetadata[databaseMetadataItem.containerId] !== undefined) {
          containerName = containerMetadata[databaseMetadataItem.containerId].name;
          containerId = containerMetadata[databaseMetadataItem.containerId].id;
          containerType = containerMetadata[databaseMetadataItem.containerId].type;
        }
      }

      let graphQLId = searchResultItem.graphQLId;
      if (!graphQLId) {
        graphQLId = databaseMetadataItem.graphQLId;
      }

      return {
        ...searchResultItem,
        graphQLId: graphQLId,
        __typename: databaseMetadataItem ? databaseMetadataItem.dbType : '',
        downstreamDataSourceCount: databaseMetadataItem ? databaseMetadataItem.downstreamDataSourceCount : undefined,
        downstreamWorkbookCount: databaseMetadataItem ? databaseMetadataItem.downstreamWorkbookCount : undefined,
        filePath: databaseMetadataItem ? databaseMetadataItem.filePath : undefined,
        location: {
          name: containerName,
          id: containerId,
          type: containerType as LocationTypes,
        },
        dataQualityWarnings: dataQualityWarnings.map((dqw) => {
          const dataQualityWarning: IDataQualityWarningInfo = {
            author: mapDataQualityWarningOwnerToAuthor(dqw.owner),
            owner: mapDataQualityWarningOwnerToAuthor(dqw.owner),
            authorDisplayName: dqw.owner?.displayName || '',
            isActive: dqw.isActive,
            isSevere: dqw.isSevere,
            message: dqw.message,
            vizportalId: dqw.id,
            warningType: dqw.type,
            updatedAt: mapDataQualityWarningUpdatedAt(dqw.updatedAt),
            assetUrl: (url: string) => {
              return databaseMetadataItem.filePath;
            },
            assetName: crassDataItem.name || '',
            assetType: databaseMetadataItem.dbType,
          };

          return dataQualityWarning;
        }),
        dataQualityCertifications: dataQualityCertifications.map((dqc) => {
          const dataQualityCertification: IDataQualityWarningInfo = {
            author: mapDataQualityWarningOwnerToAuthor(dqc.owner),
            owner: mapDataQualityWarningOwnerToAuthor(dqc.owner),
            authorDisplayName: dqc.owner?.displayName || '',
            isActive: dqc.isActive,
            isSevere: dqc.isSevere,
            message: dqc.message,
            vizportalId: dqc.id,
            warningType: dqc.type,
            updatedAt: mapDataQualityWarningUpdatedAt(dqc.updatedAt),
            assetUrl: (url: string) => {
              return databaseMetadataItem.filePath;
            },
            assetName: crassDataItem.name || '',
            assetType: databaseMetadataItem.dbType,
          };

          return dataQualityCertification;
        }),
        isCertified: databaseMetadataItem ? databaseMetadataItem.isCertified : false,
        certifier: databaseMetadataItem ? mapCertifier(databaseMetadataItem.certifier) : mapCertifier(),
        certificationNote: databaseMetadataItem ? databaseMetadataItem.certificationNote : undefined,
        hostName: databaseMetadataItem ? databaseMetadataItem.hostName : undefined,
      };
    });
  },
  getMetaDataRequests: GetMetaDataRequests.GetDatabasesRequest,
  getSortOptions: () => [SortBy.getRelevancySortSelection(), SortBy.getWorkbookPopularity()],
  quickFilters: [ConnectedTagFilter],
  shouldBeAvailable: () => SessionManagementService.instance.catalogingOnSiteEnabled(),
  favoritable: () => false,
};
